import Button from "@/ui/Buttons/Button";
import Auth from "@/Auth/Auth";
import {useLogout} from '@/Auth/useLogout';
import {tabSession} from '@/Auth/session';
import {ACCOUNT_STORAGE_CLEAR_EFFECTS} from '@/lib/accountLocalStorage';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Icons from "@/ui/IconButton/Icons";
import IconButton from "@/ui/IconButton/IconButton";
import React, {useCallback, useLayoutEffect, useState} from "react";
import type {RootState} from "@/@types/store";
import {confirmWarningOkCancel} from '@/ui/Confirm';
import {doFullRefresh} from '@/queues/polling';
import localforage from 'localforage';
import * as idb from "@/lib/idbd";
import type {StoreResult} from "@/lib/idbd";
import {connect} from "react-redux";
import Alert from "@/lib/alert";
import ErrorBoundaryO from "@/ErrorBoundary/ErrorBoundaryO";
import type {EntityType} from "@/@types/entity";
import {cast} from "@/entities/schemas";
import util from "@/lib/util";
import {MAX_FATAL_ATTEMPTS, processQueue} from "@/queues/processQueue";
import {isHeldDocumentStatus} from '@/lib/recoverableErrors';
import {EnvironmentResult} from "../Support";
import useComponentCache from "@/hooks/useComponentCache";

// Icon colors carry the severity the old colored buttons showed.
const WARNING_ICON = 'var(--sl-color-warning-700)';
const DANGER_ICON = 'var(--sl-color-danger-600)';

type CacheManagementState = {
    isClearingBackups: boolean,
}

export function hasQueuedDocumentContents(entry: any): boolean {
    const handle = entry.fileHandle;
    return handle instanceof Blob || typeof handle?.getFile === 'function' ||
        (handle?.queuedFileBytes && handle.bytes instanceof ArrayBuffer) ||
        !!entry.buffer || entry.plainText != null;
}

/** Reconstruct the original local payload for a user-initiated recovery copy. */
export async function queuedDocumentContents(entry: any): Promise<Blob> {
    let contents: Blob | undefined;
    const handle = entry.fileHandle;
    if (handle instanceof Blob) contents = handle;
    else if (handle?.queuedFileBytes && handle.bytes instanceof ArrayBuffer) {
        contents = new Blob([handle.bytes], {type: handle.type || entry.contentType || 'application/octet-stream'});
    }
    else if (typeof handle?.getFile === 'function') contents = await handle.getFile();
    else if (entry.buffer && entry.contentType?.startsWith('text/')) {
        contents = new Blob([entry.buffer], {type: entry.contentType});
    }
    else if (entry.buffer) {
        contents = new Blob([Uint8Array.from(atob(entry.buffer), char => char.charCodeAt(0))],
            {type: entry.contentType || 'application/octet-stream'});
    }
    else if (entry.plainText != null) contents = new Blob([entry.plainText], {type: 'text/plain'});
    if (!contents) throw new Error('No local document contents are available to download.');
    return contents;
}

/** Export the locally retained contents without deleting the queue row. */
export async function downloadQueuedDocument(entry: any): Promise<void> {
    const contents = await queuedDocumentContents(entry);
    const url = URL.createObjectURL(contents);
    const link = document.createElement('a');
    link.href = url;
    link.download = entry.fileName || `document-${entry.documentId || entry.addedOn}`;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function CacheManagement(props: CacheManagementProps) {
    const logout = useLogout();
    const {cacheValue, cacheSave} = useComponentCache<CacheManagementState>('cache-management');

    const [isClearingBackups, setIsClearingBackups] = useState<boolean>(cacheValue('isClearingBackups', false));
    const [downloadedDocuments, setDownloadedDocuments] = useState<Set<number>>(() => new Set());
    const [discardedDocuments, setDiscardedDocuments] = useState<Set<number>>(() => new Set());

    const isServerOnline = props.environment?.connectivity.isApiServerConnected;
    const isInternetOnline = props.environment?.connectivity.isOnline;

    // Save cache
    useLayoutEffect(() => {
        return () => {
            cacheSave({isClearingBackups});
        }
    }, [cacheSave, isClearingBackups]);

    /**
     * Get queues
     *
     * @param {StoreResult} queues
     */
    const getWarnings = useCallback((queues: StoreResult) => {
        // Check if there are any uncommitted items in the queues.  If so, report the number and type to the user
        // as a warning, and color the Clear Caches button appropriately
        const cacheKeys = Object.keys(queues);

        return cacheKeys.reduce((found, key) => {
            const entries = key === 'document' ? queues[key]?.filter(entry => !discardedDocuments.has(entry.addedOn)) : queues[key];
            if (Array.isArray(entries) && entries.length > 0) {
                return found.concat([{key, entries}]);
            }
            else return found;
        }, []);
    }, [discardedDocuments]);

    const renderWarningCell = useCallback((key: string, entries: any[]) => {
        const numValues = entries.length;

        const castedEntries: EntityType[] = entries.map(t => cast(key, t));

        return (
            <tr className='table-warning' key={key}>
                {/* Type */}
                <td>{util.capitalize(key)}</td>

                {/* # Entries */}
                <td className='table-centered'>{numValues}</td>

                {/* Description */}
                <td className='table-icons'>
                    {entries.map((entry, i) => {
                        let label = entry.displayFn?.() || entry.name || entry.label || entry.invoiceNumber || key;
                        if (typeof (label) === 'object') label = JSON.stringify(label);
                        return <div key={`warning-cell-${entry.id || entry.addedOn}-${i}`} style={{marginBottom: 5, color: entry.fatalError?.message ? 'var(--sl-color-danger-600)' : 'inherit'}}>
                            {label}
                            {entry.supersededByAddedOn && <div>Preserved earlier document version</div>}
                        </div>;
                    })}
                </td>

                {/* Errors */}
                <td className='table-errors'>
                    {castedEntries.map((entry, i) => {
                        if (entry.fatalError?.message && (key === 'document' || !entry.fatalError.recoverable || entries[i]?.supersededByAddedOn)) {
                            return <div key={`warning-cell-casted-${entry?.id || entry?.addedOn}-${i}`} style={{marginBottom: 5, minWidth: '10em'}}>
                                {entry.fatalError.message}
                                {key === 'document' && !entries[i]?.supersededByAddedOn &&
                                    <div>{isHeldDocumentStatus(entry.fatalError.status)
                                        ? 'Needs correction.'
                                        : (entries[i]?.retryAttempts ?? 0) >= MAX_FATAL_ATTEMPTS
                                          ? 'Automatic retries stopped. Use Retry Cache or contact support.'
                                          : 'The app will retry automatically.'}</div>}
                            </div>
                        }
                        else return <div key={`warning-cell-casted-${entry?.id || entry?.addedOn}-${i}`} style={{marginBottom: 5, minWidth: '10em'}}>&nbsp;</div>;
                    })}
                </td>

                {/* Local recovery for documents that could be the only copy. */}
                <td>
                    {entries.map((entry, i) => <div key={`recovery-${entry.documentId || entry.addedOn}-${i}`} style={{marginBottom: 5}}>
                        {key === 'document' && <>
                            {(entry.plainText || (entry.contentType?.startsWith('text/') && entry.buffer)) &&
                                <details><summary>View local text</summary><pre style={{whiteSpace: 'pre-wrap'}}>{entry.plainText || entry.buffer}</pre></details>}
                            <Button label="Download copy" disabled={!hasQueuedDocumentContents(entry)} onClick={() => {
                                void downloadQueuedDocument(entry).then(() =>
                                    setDownloadedDocuments(current => new Set(current).add(entry.addedOn))
                                ).catch(error => Alert.addSimpleError(error.message));
                            }}/>
                            {!hasQueuedDocumentContents(entry) && <div>This queued change has no local file or note to download.</div>}
                            {(entry.fatalError || entry.supersededByAddedOn) &&
                                <Button label="Discard this version" severity='danger'
                                    disabled={hasQueuedDocumentContents(entry) && !downloadedDocuments.has(entry.addedOn)} onClick={() => {
                                        const operation = tabSession.capture();
                                        confirmWarningOkCancel('Discard this local document version?',
                                            hasQueuedDocumentContents(entry)
                                                ? 'Check that your downloaded copy is saved first. This permanently removes only this queued version for every tab using this firm and user.'
                                                : 'This queued change has no local file or note. Discarding it permanently removes this version for every tab using this firm and user.',
                                            'Discard this version', 'Cancel', () => {
                                                void (async () => {
                                                    operation.assertCurrent();
                                                    const result = await idb.remove('document', entry.addedOn);
                                                    operation.assertCurrent();
                                                    if (result.isErr()) throw result.unwrapErr();
                                                    setDiscardedDocuments(current => new Set(current).add(entry.addedOn));
                                                })().catch(error => {
                                                    if (!operation.signal.aborted) Alert.addSimpleError(error.message);
                                                });
                                            });
                                    }}/>}
                        </>}
                    </div>)}
                </td>
            </tr>
        );
    }, [downloadedDocuments]);

    // Get any warning for the storage
    const warnings = getWarnings(props.environment.storage.queues);

    // What is cached?
    const cacheTable = warnings.length ? warnings.map(w => renderWarningCell(w.key, w.entries)) : undefined;

    // Get the total number of warnings
    const numEntries = warnings.reduce((total, w) => total + w.entries.length, 0);
    const activeEntries = warnings.reduce((total, w) => total + w.entries.filter(entry => !entry.supersededByAddedOn).length, 0);
    const hasQueuedDocuments = warnings.some(w => w.key === 'document' && w.entries.some(hasQueuedDocumentContents));

    const onRetryCache = useCallback(async () => {
        const operation = tabSession.capture();
        // Strip error information from all objects in the cache and try to save them to the database.
        const result = await idb.resetAll();
        if (operation.signal.aborted) return;
        if (result.isOk()) await processQueue();
        else Alert.addSimpleError(result.unwrapErr().message);
    }, []);

    // Construct a warning
    let clearCacheWarning = (
        <div>
            <div>Clear out any changes that have yet to be uploaded to the cloud.</div>
            <div>This includes not just new items, but changes and deletes you have made to existing items.
            </div>
            {warnings.length > 0 &&
                <div className='support-form--cache-warning'>
                    {`You have ${numEntries} local entr${numEntries > 1 ? 'ies' : 'y'}, including preserved document versions (see below).`}
                    {' '}Clearing them also deletes any notes or files that exist only on this device. Download a copy first.
                </div>
            }
        </div>
    );


    /**
     * Reload data from the server
     */
    const onRefreshData = useCallback(() => {
        doFullRefresh('onRefreshData');
    }, []);

    /**
     * Clear out anything in IndexedDB
     */
    const onClearUncommittedChanges = useCallback(() => {
        const operation = tabSession.capture();
        // Warn the user that we are removing precious data
        confirmWarningOkCancel(
            'Clear uncommitted changes?',
            <p>This permanently deletes all queued changes for this firm and user, including new items, edits, and deletions awaiting upload. Other tabs using the same account share this queue and will lose these queued changes too. Close those tabs first. This cannot be undone.</p>,
            'OK', 'Cancel',
            () => {
                try { operation.assertCurrent(); }
                catch { Alert.addSimpleError('The session changed. Reopen this action for the current account.'); return; }
                idb.clearErrors()
                    .then(clearResult => {
                        if (operation.signal.aborted) return;
                        if (clearResult.isErr()) Alert.addSimpleError(clearResult.unwrapErr().message);
                    })
                    .catch(console.error);
            },
            () => {
            }
        );
    }, []);

    const onClearBackups = useCallback(() => {
        const operation = tabSession.capture();
        const {firmId, userId} = operation.identity;
        confirmWarningOkCancel('Clear this account’s offline backups?',
            'Other tabs using this firm and user share these backups and may recreate them. Save work and close those tabs first. This removes downloaded data used for offline loading; queued changes and saved drafts remain. A full download will start in this tab.',
            'Clear backups', 'Cancel', () => {
                void (async () => {
                    operation.assertCurrent();
                    if (!Auth.canLogout()) return;
                    setIsClearingBackups(true);
                    document.body.style.cursor = 'wait';
                    const backups = localforage.createInstance({
                        name: `Backups_${firmId}_${userId}`, storeName: `Backups_${firmId}_${userId}`,
                        driver: localforage.INDEXEDDB,
                    });
                    await backups.clear();
                    operation.assertCurrent();
                    doFullRefresh('onClearBackups');
                })().catch(() => {
                    if (!operation.signal.aborted) Alert.addSimpleError('The offline backups could not be cleared. Keep this tab open and try again.');
                }).finally(() => {
                    if (operation.signal.aborted) return;
                    setIsClearingBackups(false);
                    document.body.style.cursor = 'default';
                });
            });
    }, [setIsClearingBackups]);

    /**
     * Clear out the application cache and local/session storage
     *
     * @param e
     */
    const onClearCaches = useCallback(() => {
        const operation = tabSession.capture();
        confirmWarningOkCancel(
            'Reset account preferences and log out?',
            <p>{ACCOUNT_STORAGE_CLEAR_EFFECTS} This tab will log out after the reset.</p>,
            'Reset and log out', 'Cancel',
            () => {
                try {
                    operation.assertCurrent();
                    void logout(operation.identity.portal, false, true);
                } catch {
                    Alert.addSimpleError('The session changed. Reopen this action for the current account.');
                }
            },
            () => {
            }
        );
    }, [logout]);

    return <section id='cache-management'>
        {/* Refresh Data from Cloud */}
        <article className="support-form__buttons">
            <IconButton type={props.statusBar.isFetching ? Icons.SYNC_LIGHT_SPIN : Icons.SYNC_LIGHT}
                        label="Refresh Data"
                        onClick={onRefreshData}
                        disabled={!isServerOnline || !isInternetOnline || !Auth.isAuthenticated() || props.statusBar.isFetching}
            />
            <aside>
                {props.statusBar.isFetching && <div>Loading...</div>}
                {!props.statusBar.isFetching &&
					<div>Starts a background reload of all data
						from the cloud. This is the same as clicking on the <FontAwesomeIcon icon={Icons.SYNC_LIGHT.reactIcon} color='#D66C16'/> Sync
						button in the toolbar.
					</div>
                }
            </aside>
        </article>

        {/* Only clear the backup cache */}
        <article className="support-form__buttons">
            <IconButton type={Icons.DATABASE}
                        label="Clear Backups"
                        color={WARNING_ICON}
                        onClick={onClearBackups}
                        disabled={isClearingBackups}
            />
            <aside>
                Clears offline backups shared by tabs using this firm and user. Queued changes and saved drafts remain.
                You stay signed in, and a full download starts in this tab.
            </aside>
        </article>

        {/* Clear application cache and local storage */}
        <article className="support-form__buttons">
            <IconButton type={Icons.OPTION_MENU}
                        label="Reset Account Preferences"
                        color={DANGER_ICON}
                        onClick={onClearCaches}
            />
            <aside>
                Resets this firm and user’s saved browser preferences and cached firm details, including in other tabs using the same account.
                This tab logs out. Queued changes, saved drafts and offline backups remain on this device.
            </aside>
        </article>

        {/* Clear uncommitted changes */}
        <article className="support-form__buttons">
            <IconButton type={Icons.ERASER}
                        label="Clear Uncommitted"
                        color={DANGER_ICON}
                        onClick={onClearUncommittedChanges}
                        disabled={!warnings.length || hasQueuedDocuments}
            />
            <aside>{clearCacheWarning}{hasQueuedDocuments && <p>Download and resolve the document versions below before clearing other queued changes.</p>}</aside>
        </article>

        <hr/>

        {/* Cached entries */}
        <ErrorBoundaryO uniqueKey='cached-entries'>
            <article>
                <h3>Queued and Preserved Entries</h3>
                {activeEntries > 0 && <div className="support-form__retry" style={{marginBottom: '1em'}}>
					<IconButton type={Icons.RETRY} label='Retry Cache' color={WARNING_ICON} onClick={onRetryCache}/>
				</div>}

                {cacheTable && (cacheTable.length > 0) ?
                 <div className="support-form__table">
                     <table>
                         <thead>
                         <tr>
                             <th>Type</th>
                             <th>Entries</th>
                             <th>Description</th>
                             <th>Errors</th>
                             <th>Recovery</th>
                         </tr>
                         </thead>
                         <tbody>{cacheTable}</tbody>
                     </table>
                 </div> :
                 <aside style={{color: 'darkgreen'}}>No entries in the cache</aside>
                }
            </article>
        </ErrorBoundaryO>
    </section>
}

const mapStateToProps = (state: RootState) => ({
    statusBar: state.statusBar,
});

export type CacheManagementProps = Pick<RootState, 'statusBar'> & {
    environment: EnvironmentResult;
}

export default connect(mapStateToProps)(CacheManagement);
